/**
 * **콜팝 운영사·관리점 축의 판정·변환 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 문구는 [callpopText.js](./callpopText.js) 가, chip·참여 어휘는 [partnerLink.js](./partnerLink.js) 가
 * 갖는다. 이 파일에 있는 것은 **서버 응답을 화면이 읽는 모양으로 바꾸는 순수 함수**와 경로·액션 id 다.
 *
 * ## 여기에 없는 것
 *
 * - **권한 판정** — 「부를 수 있는가」는 세션의 `sa_actions` 뿐이고 술어는 `utils/authActions.hasAction`
 *   한 곳이다. 역할 문자열 비교는 하지 않는다(`utils/adminRole` 밖에 사본을 두지 않는다).
 * - **코드 정규화·유일성·참여 조건 판정** — 서버가 정본이다(`AgencyCodeRules`·`CallpopJoinEligibility`).
 *   화면은 응답을 보이기만 한다. 사본을 두면 서버가 규칙을 고칠 때 화면이 조용히 거짓말을 한다.
 * - **가시 범위** — 서버가 좁힌다. 못 보는 대상은 404 로 오고([CALLPOP_TEXT.notFound]) 목록은 그대로 그린다.
 *
 * 계약 정본: `ordern-workspace/docs/kt-callpop/briefs/B7b-운영사관리점-appserver-report.md` §3.
 */
import { CALLPOP_TEXT } from './callpopText.js'
import { numberText } from './formatterNum.js'
import { toNumberOrNull } from './numberValue.js'
import { PARTICIPATION_STATE } from './partnerLink.js'
import { getDateRangeForPeriod } from './periodFilter.js'
import { without } from './without.js'

/** 어드민 API base — 경로 리터럴을 화면마다 적지 않는다(`partnerLink.PARTNER_LINK_API` 와 같은 자리) */
export const CALLPOP_API = '/callpop'

/** 화면 경로 — 라우터·홈 표·메뉴 SQL 이 같은 문자열을 본다 */
export const CALLPOP_PATH = Object.freeze({
  ORG: '/v/callpop/org',
  STORES: '/v/callpop/stores',
  STORE_NEW: '/v/callpop/stores/new',
  HISTORY: '/v/callpop/history',
  /** 「콜팝 가입신청」(콜팝 웹 가입 §11.9) — 사이드 메뉴 행은 appserver 메뉴 SQL 이 이 경로로 넣는다 */
  APPLICATIONS: '/v/callpop/applications',
})

/**
 * **액션 id** — SQL `023` §6 이 API 23개마다 등록한 `sys_action.action_id` 와 같은 문자열이다.
 *
 * 버튼·라우트·호출을 전부 이것으로 가른다. **리소스가 없는 호출은 403 이 아니라 401 이고 그 401 이 세션을
 * 끊는다**(서버 보고서 §10 3) — 「눌렀더니 로그아웃」을 막는 유일한 방법은 부르지 않는 것이다.
 */
export const CALLPOP_ACTION = Object.freeze({
  OPERATOR_SEARCH: 'CallpopOperatorSearch',
  OPERATOR_ADD: 'CallpopOperatorAdd',
  OPERATOR_UP: 'CallpopOperatorUp',
  OPERATOR_OFF: 'CallpopOperatorOff',
  OPERATOR_ON: 'CallpopOperatorOn',
  OPERATOR_WAIVE: 'CallpopOperatorWaive',
  AGENCY_SEARCH: 'CallpopAgencySearch',
  AGENCY_LOOKUP: 'CallpopAgencyLookup',
  AGENCY_ADD: 'CallpopAgencyAdd',
  AGENCY_IMPORT: 'CallpopAgencyImport',
  AGENCY_UP: 'CallpopAgencyUp',
  AGENCY_OFF: 'CallpopAgencyOff',
  AGENCY_ON: 'CallpopAgencyOn',
  AGENCY_STORE_GET: 'CallpopAgencyStoreGet',
  AGENCY_STORE_ADD: 'CallpopAgencyStoreAdd',
  STORE_SEARCH: 'CallpopStoreSearch',
  STORE_MAPPING: 'CallpopStoreMapping',
  STORE_MAPPING_LOG: 'CallpopStoreMappingLog',
  STORE_PG_CONTRACT: 'CallpopStorePgContract',
  BIZ_NO_CHECK: 'CallpopBizNoCheck',
  ORG_HISTORY_GET: 'CallpopOrgHistoryGet',
  AUTO_JOIN_RUN: 'CallpopAutoJoinRun',
  JOIN_BULK: 'CallpopJoinBulk',
})

/**
 * 콜팝 쓰기의 axios config — **이 403 은 세션을 끝내지 않는다**(규약 §16.170 옵트아웃).
 *
 * 액션 게이트가 「부를 수 없는 호출」을 앞에서 막으므로 여기 남는 403 은 **업무 계층의 답**뿐이다:
 * 이관 자격(`자기 운영사 소속 매장만…`)·마스터 쓰기 자격처럼 액션은 있는데 그 **대상**이 범위 밖인 경우다
 * (서버 보고서 §3-15 403 행). 대체 동작은 서버 문구를 그대로 보이는 것이라 옵트아웃 조건을 만족한다.
 * `showErrorMessage` 는 끄지 않는다 — 그 한글 문장이 곧 화면의 답이다.
 */
export const CALLPOP_WRITE_CONFIG = Object.freeze({ logoutOn403: false })

/**
 * **폼이 그 실패를 자기 안에서 말하는 쓰기**의 config — 토스트를 끈다(§16.175 옵트아웃).
 *
 * 다이얼로그 여섯(운영사·관리점 등록/수정, 매핑, 비활성 이관, 엑셀, 신규 매장 등록)은 서버 문장을
 * **폼 안에** 앉힌다. 인터셉터의 빨간 토스트가 함께 뜨면 같은 문장이 두 번 보이고, 사람은 둘 중
 * 어느 것이 지금 폼의 상태인지 묻게 된다.
 *
 * **[CALLPOP_WRITE_CONFIG] 와 나눠 둔 이유**: 같은 축의 쓰기라도 확인창만 띄우고 끝나는 것들
 * (운영사 비활성·재활성·권리 포기·PG 계약일·자동 참여 실행)은 **폼이 없어** 토스트가 유일한 답이다.
 * 한 상수에 `showErrorMessage:false` 를 넣으면 그 자리들이 조용히 실패한다 — 옵트아웃은 언제나
 * 「이 호출부가 이미 답을 갖고 있다」는 선언이고, 갖지 않은 자리에 붙이면 그 선언이 거짓이 된다.
 */
export const CALLPOP_FORM_WRITE_CONFIG = Object.freeze({ logoutOn403: false, showErrorMessage: false })

/** 목록 조회 spec — `plugins/http/permittedRead` 가 게이트와 옵트아웃을 함께 갖는다 */
export const OPERATOR_LIST_READ = Object.freeze({
  action: CALLPOP_ACTION.OPERATOR_SEARCH,
  url: `${CALLPOP_API}/operators`,
  showLoading: false,
})

/**
 * 관리점 목록 spec — `GET /callpop/agencies`(부분 일치 `q`·`operatorCd`·`useYn`). **`useYn` 을 주지 않으면
 * 비활성도 온다**(서버 기본값 = 전부). 그래서 과거 조직을 지목해야 하는 자리(변경 이력)는 이것을 원천으로 쓴다 —
 * [AGENCY_LOOKUP_READ] 는 두 갈래 모두 **활성만** 준다(R-A §1-1).
 */
export const AGENCY_LIST_READ = Object.freeze({
  action: CALLPOP_ACTION.AGENCY_SEARCH,
  url: `${CALLPOP_API}/agencies`,
  showLoading: false,
})

/** 관리점 원격 검색 spec — 명칭 검색 + 코드 해석이 같은 엔드포인트다(결정 23). **활성 관리점만** 준다(R-A §1-1) */
export const AGENCY_LOOKUP_READ = Object.freeze({
  action: CALLPOP_ACTION.AGENCY_LOOKUP,
  url: `${CALLPOP_API}/agencies/search`,
  showLoading: false,
})

/** 매장 입점 현황의 `size` 상한 — 넘기면 서버가 400 이다(§3-4) */
export const STORE_PAGE_SIZE_MAX = 200

/** 페이지 크기 선택지 — 상한을 넘는 값을 고를 자리를 만들지 않는다 */
export const STORE_PAGE_SIZES = Object.freeze([50, 100, STORE_PAGE_SIZE_MAX])

/** 이력·목록의 기본 페이지 크기(서버 기본과 같다) */
export const CALLPOP_PAGE_SIZE = 50

/**
 * 일괄 참여 한 요청의 크기 — 서버 상한은 500 이지만 **화면은 100 씩 끊어 보낸다**(서버 보고서 §10 5:
 * 매장마다 Redis 락 + 독립 트랜잭션이라 500건이면 락 왕복이 500회다). 쪼개기는 `partnerLink.chunkStoreCds`
 * 가 이미 하는 일이라 다시 적지 않는다.
 */
export const JOIN_BULK_CHUNK = 100

/** 엑셀 양식의 헤더 1행 — 서버 `AgencyImportRules` 가 이 순서를 강제한다(§3-12). 문자열 사본을 두지 않는다 */
export const AGENCY_IMPORT_HEADER = Object.freeze(['운영사코드', '관리점코드', '관리점명', '담당자', '휴대폰', '메모'])

/** 엑셀 한 번의 행 상한(§3-12) */
export const AGENCY_IMPORT_MAX_ROWS = 1000

export const AGENCY_IMPORT_ACCEPT = '.xlsx,.xls'

/** 엑셀 업로드의 파트 이름 — 서버가 `file` 로 받는다 */
export const AGENCY_IMPORT_PART = 'file'

/** 이력의 대상 종류 — 서버 `targetTp` 와 같은 어휘 */
export const HISTORY_TARGET = Object.freeze({
  OPERATOR: 'OPERATOR',
  AGENCY: 'AGENCY',
  STORE_MAP: 'STORE_MAP',
})

/** 이력의 동작 9종 — 서버 `action` 과 같은 어휘 */
export const HISTORY_ACTION = Object.freeze({
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  DEACTIVATED: 'DEACTIVATED',
  REACTIVATED: 'REACTIVATED',
  IMPORTED: 'IMPORTED',
  MAPPED: 'MAPPED',
  TRANSFERRED: 'TRANSFERRED',
  WAIVED: 'WAIVED',
  UNMAPPED: 'UNMAPPED',
})

/** 매핑 직후 자동 참여의 5갈래(§3-5) */
export const AUTO_JOIN = Object.freeze({
  JOINED: 'JOINED',
  ALREADY_ACTIVE: 'ALREADY_ACTIVE',
  ENDED: 'ENDED',
  INELIGIBLE: 'INELIGIBLE',
  FAILED: 'FAILED',
})

/** 사업자번호 조회의 결과 어휘(§3-7) */
export const BIZ_NO_CHECK = Object.freeze({
  NEW: 'NEW',
  EXISTS_VISIBLE: 'EXISTS_VISIBLE',
  EXISTS_HIDDEN: 'EXISTS_HIDDEN',
  INVALID: 'INVALID',
})

/** 신규 매장 등록의 두 갈래(§3-8) */
export const REGISTER_MODE = Object.freeze({ NEW: 'NEW', SIBLING: 'SIBLING' })

/**
 * 관리점 후보 셀렉트에 **세우는** 옵션 수의 상한 — 묶인 것은 세지 않는다([keepSelectedOptions]).
 *
 * `GET /callpop/agencies/search` 는 질의어가 비면 **범위 안 활성 관리점 전부**를 준다(서버 §3-3).
 * 운영사 하나가 수백 곳을 가질 수 있고, 총괄·내부는 그 전부를 본다. 상한의 정본은 서버지만 그 전까지
 * 화면이 수백 개의 옵션 노드를 그리지 않게 앞에서 자른다 — `adminRole.PLACE_OPTION_MAX` 와 같은 규칙이고,
 * **이미 고른 값은 이 상한과 무관하게 남는다**(잘리면 태그가 PK 숫자로 되돌아간다).
 */
export const AGENCY_OPTION_MAX = 200

/** 신규 매장 등록이 사업자번호 때문에 막힌 409 의 사유 코드(서버 `CallpopBizNoConflict`) */
export const BIZ_NO_CONFLICT = Object.freeze({
  SIBLING_REQUIRED: 'SIBLING_REQUIRED',
  EXISTS_HIDDEN: 'BIZNO_EXISTS_HIDDEN',
})

/**
 * 관리점 등록이 막힌 409 의 사유 코드(서버 `CallpopAgencyConflict`, R-A §1-3) — 이 코드의 409 만 대상
 * `agencyCd`·`agencyCode` 를 싣는다. 나머지 409(코드 충돌 등)는 공용 봉투(`C013`)이고 문장만 있다.
 */
export const AGENCY_CONFLICT = Object.freeze({
  INACTIVE_SAME_MOBILE: 'AGENCY_INACTIVE_SAME_MOBILE',
})

/** 참여 조건 미달 사유 — 400 `JOIN_INELIGIBLE` 의 `reasons` 와 일괄 결과의 `skipped[].reasons` */
export const INELIGIBLE_REASON = Object.freeze({
  NO_MID: 'NO_MID',
  NO_MENU: 'NO_MENU',
  TRANSITION_FAILED: 'TRANSITION_FAILED',
})

// ─── 경로 ────────────────────────────────────────────────────────────────

export function operatorUrl(operatorCd, action) {
  const base = `${CALLPOP_API}/operators`
  if (operatorCd == null) return base
  return action ? `${base}/${operatorCd}/${action}` : `${base}/${operatorCd}`
}

export function agencyUrl(agencyCd, action) {
  const base = `${CALLPOP_API}/agencies`
  if (agencyCd == null) return base
  return action ? `${base}/${agencyCd}/${action}` : `${base}/${agencyCd}`
}

export function agencyStoresUrl(agencyCd) {
  return `${agencyUrl(agencyCd)}/stores`
}

export function storeMappingUrl(storeCd) {
  return `${CALLPOP_API}/stores/${storeCd}/mapping`
}

export function storeMappingHistoryUrl(storeCd) {
  return `${storeMappingUrl(storeCd)}/history`
}

export function storePgContractUrl(storeCd) {
  return `${CALLPOP_API}/stores/${storeCd}/pg-contract`
}

export function agencyImportUrl(dryRun) {
  return `${CALLPOP_API}/agencies/import?dryRun=${dryRun ? 'true' : 'false'}`
}

// ─── 응답 읽기 ───────────────────────────────────────────────────────────

/**
 * 목록 봉투 — `{items, page, size, totalCount}`. 이상한 응답에도 값을 돌려준다(화면이 깨지지 않는다).
 *
 * 콜팝 API 의 봉투는 어드민 표준(`content`·`totalElements`·`number`)과 **모양이 다르다** — `utils/pagedList`
 * 를 빌려 쓰면 두 계약이 한 함수에서 섞인다. 읽는 자리를 여기 하나로 둔다.
 */
export function pageEnvelope(result) {
  const row = result && typeof result === 'object' ? result : {}
  const items = Array.isArray(row.items) ? row.items.slice() : []

  return {
    items,
    page: typeof row.page === 'number' ? row.page : 0,
    size: typeof row.size === 'number' ? row.size : items.length,
    totalCount: typeof row.totalCount === 'number' ? row.totalCount : items.length,
  }
}

/** `{items}` 만 쓰는 응답(운영사·관리점 검색·이력)의 항목 */
export function itemsOf(result) {
  return pageEnvelope(result).items
}

/**
 * 이력 응답의 **이름 사전**(§3-5 `refs`) — `{operators, agencies, stores}`, 키는 **문자열 PK** 다.
 *
 * 이력 행은 불변이라 PK 만 담는다(`fromOperatorCd`·`toAgencyCd`·`targetCd`). 그 PK 를 이름으로 푸는 일을
 * 화면이 마스터를 다시 읽어서 하지 않는다 — 서버가 한 쪽에 한 번 붙여 준다. 없는 축은 빈 객체라 호출부가
 * 존재를 묻지 않는다.
 */
export function refsOf(result) {
  const row = result && typeof result === 'object' ? result : {}
  const refs = row.refs && typeof row.refs === 'object' ? row.refs : {}

  return {
    operators: refs.operators || {},
    agencies: refs.agencies || {},
    stores: refs.stores || {},
  }
}

/**
 * 사전에서 이름을 찾는다 — **없으면 `null`**(호출부가 코드를 그대로 보인다).
 *
 * 이름은 **지금 마스터의 값**이고 이력 시점의 이름이 아니다(서버 §3-5). 지워진 대상은 사전에 없으므로
 * 화면은 그때 코드를 그대로 보인다 — 지어내지 않는다.
 *
 * @param {?object} refs [refsOf] 의 결과
 * @param {'operators'|'agencies'|'stores'} axis
 * @param {*} cd PK
 */
export function refLabel(refs, axis, cd) {
  if (cd == null) return null
  const row = ((refs && refs[axis]) || {})[String(cd)]
  if (!row) return null

  return orgLabel(row.name || row.storeNm, row.code || row.storeId) || null
}

// ─── 운영사 마스터 쓰기 자격 ─────────────────────────────────────────────

/**
 * `GET /callpop/operators` 의 **능력 값**(B7-b 후속 §2-2) — `{manageOperators, designateHq}`.
 *
 * URL 권한은 **역할 단위**라 총괄사에게 운영사 쓰기를 열면 같은 역할인 일반 운영사에게도 열린다. 「이
 * 계정이 마스터를 쓸 수 있는가」는 총괄 운영사 축인지에 달렸고 그것은 서버만 아는 사실이라, 서버가 목록과
 * 함께 답을 내린다 — 화면은 그 값을 옮길 뿐 역할 이름으로 다시 판정하지 않는다(파일 머리말의 규칙).
 *
 * **값이 없는 응답(옛 서버)은 막지 않는다**: 그때의 답은 「모른다」이지 「할 수 없다」가 아니다. 배포
 * 순서가 서버 먼저라 화면이 앞설 일은 없지만, 앞선다면 지금 동작(액션 권한만)으로 떨어진다.
 *
 * **이 함수는 「응답이 왔다」를 전제한다.** 아직 안 읽었거나 못 읽은 상태는 값이 아니라 `null` 이고
 * (`callpopOperatorsMixin.operatorCapabilities`), 그때 [operatorWriteAbility] 는 마스터 쓰기를 전부 닫는다 —
 * 두 상태를 한 값으로 접으면 첫 조회가 끝나기 전 구간에 **거짓 버튼**이 선다(리뷰 §1).
 *
 * 「부재 = 허용」의 해석은 **여기 한 곳**이다. 돌려주는 값은 정규화된 boolean 둘이고, 쓰는 쪽은 그것을
 * 그대로 곱한다 — 폴백 규칙이 두 함수에 있으면 정책이 바뀌는 날 한쪽만 고쳐진다(리뷰 §2).
 */
export function operatorCapabilitiesOf(result) {
  const row = result && typeof result === 'object' ? result : {}
  const capabilities = row.capabilities && typeof row.capabilities === 'object' ? row.capabilities : {}

  return {
    manageOperators: capabilities.manageOperators !== false,
    designateHq: capabilities.designateHq !== false,
  }
}

/**
 * 운영사 축 버튼의 **최종 자격** — 세션 액션 × 서버 능력 값. 순수 함수.
 *
 * 액션은 「이 URL 을 부를 수 있는가」(역할 단위)이고 능력 값은 「이 계정이 그 대상을 쓸 수 있는가」다.
 * 둘 다 있어야 버튼이 선다 — 액션만 보면 일반 운영사에게도 등록 버튼이 서고 그 클릭은 403 으로 끝난다.
 *
 * `capabilities` 가 **`null`(아직 못 읽었다)이면 마스터 쓰기를 전부 닫는다** — 모르는 동안 열어 두면
 * 그 구간에 선 버튼이 거짓이 된다(리뷰 §1). 폴백 해석은 [operatorCapabilitiesOf] 가 이미 했으므로
 * 여기서는 정규화된 boolean 을 곱하기만 한다(리뷰 §2).
 *
 * **`waive`(권리 포기)에는 능력 값을 곱하지 않는다**: 자기 운영사를 접는 일이라 마스터 쓰기 자격과 축이
 * 다르다(§2-1 이 말하는 것은 등록·수정·비활성·재활성 넷과 총괄 지정뿐이다).
 *
 * **총괄 축(`designateHq`)에는 자기 액션이 없다** — 조작마다 리소스가 다르기 때문이다: 총괄 행 비활성은
 * `PUT …/{cd}/deactivate`(= `off`), 등록 폼의 총괄 칸은 `POST …`(= `add`), 수정 폼의 칸은 `PUT …/{cd}`
 * (= `up`). 그래서 능력 값만 `designateHq` 로 내보내고 **곱할 액션은 쓰는 쪽이 고른다**([addHq]·[upHq]·
 * [operatorRowButtons]). 한 액션(`add`)에 묶어 두면 `Up`·`Off` 만 가진 역할이 생기는 날 총괄 칸과 총괄 행
 * 비활성이 이유 없이 사라진다(리뷰 §3).
 *
 * @param {?object} actions 세션 액션 판정 `{add, up, off, on, waive}`
 * @param {?object} capabilities [operatorCapabilitiesOf] 의 결과, 또는 아직 못 읽었으면 `null`
 * @returns {{add: boolean, up: boolean, off: boolean, on: boolean, waive: boolean,
 *   designateHq: boolean, addHq: boolean, upHq: boolean}}
 */
export function operatorWriteAbility(actions, capabilities) {
  const can = actions || {}
  const granted = capabilities || null
  const manage = !!granted && granted.manageOperators === true
  const designateHq = !!granted && granted.designateHq === true

  return {
    add: !!can.add && manage,
    up: !!can.up && manage,
    off: !!can.off && manage,
    on: !!can.on && manage,
    waive: !!can.waive,
    designateHq,
    /** 등록 폼의 「총괄사」 칸 */
    addHq: !!can.add && manage && designateHq,
    /** 수정 폼의 「총괄사」 칸 */
    upHq: !!can.up && manage && designateHq,
  }
}

/**
 * 운영사 **한 행**의 버튼 — [operatorWriteAbility] 의 자격에 그 행의 상태를 건다. 순수 함수.
 *
 * 총괄 행(`hqYn:'Y'`)의 비활성은 **총괄 지정을 바꾸는 것과 같은 자격**을 요구한다(§2-1): 총괄사 계정이
 * 자기 발판을 없애는 일이라 서버가 403 으로 막는다. 화면이 그 버튼을 세우면 클릭이 거절로 끝난다.
 * 그 자격은 **비활성 액션 × `designateHq`** 다 — 등록 액션을 묻지 않는다(리뷰 §3).
 */
export function operatorRowButtons(row, ability) {
  const can = ability || {}
  const isHq = !!row && row.hqYn === 'Y'
  const inactive = isInactiveOrg(row)

  return {
    edit: !!can.up,
    off: !!can.off && !inactive && (!isHq || !!can.designateHq),
    on: !!can.on && inactive,
    waive: !!can.waive && !isHq && !inactive,
  }
}

// ─── 선택 상자 옵션 ──────────────────────────────────────────────────────

/**
 * 운영사 → 셀렉트 옵션. **id 는 마스터 PK 의 문자열**이다 — 계정 범위 바인딩(`scopes[].id`)이 그 값이고,
 * `el-select` 의 값은 문자열로 다루어야 서버가 준 바인딩과 `indexOf` 가 맞는다.
 */
export function operatorOptionsOf(items) {
  return (Array.isArray(items) ? items : [])
    .filter((row) => row && row.operatorCd != null)
    .map((row) => ({
      id: String(row.operatorCd),
      operatorCd: row.operatorCd,
      code: row.code || '',
      name: row.operatorNm || row.name || '',
      hqYn: row.hqYn || 'N',
      useYn: row.useYn || 'Y',
      label: orgLabel(row.operatorNm || row.name, row.code),
    }))
}

/**
 * 관리점 검색 결과 → 셀렉트 옵션. `agencies/search` 의 항목(`{agencyCd, code, name, operator}`)을 읽는다.
 *
 * 그릴 수를 [AGENCY_OPTION_MAX] 로 자른다 — 질의어 없는 조회는 범위 안 전부를 준다(§3-3).
 * 고른 값은 [keepSelectedOptions] 가 상한 밖에서 지킨다.
 */
export function agencyOptionsOf(items) {
  return (Array.isArray(items) ? items : [])
    .filter((row) => row && row.agencyCd != null)
    .slice(0, AGENCY_OPTION_MAX)
    .map((row) => ({
      id: String(row.agencyCd),
      agencyCd: row.agencyCd,
      code: row.code || '',
      name: row.name || row.agencyNm || '',
      operatorCd: (row.operator && row.operator.operatorCd) != null ? row.operator.operatorCd : null,
      label: orgLabel(row.name || row.agencyNm, row.code),
    }))
}

/**
 * **새로 묶을 수 있는 조직 옵션** — 활성만 고를 수 있고, **이미 묶인 것은 비활성이어도 남긴다**. 순수 함수.
 *
 * 계정 발급은 조직 바인딩의 존재만 검사하므로(서버 `CallerScope.canBind`) 비활성 운영사를 고르면 그대로
 * 묶인다(R-A appserver 보고서 §3 (다)). 목록을 활성만으로 좁히면 막히지만, 이미 비활성 조직에 묶인 계정을
 * 열었을 때 그 값이 옵션에서 사라져 태그가 PK 숫자가 된다 — 그 계정은 재활성을 기다리는 중이고(결정 38 ⑥)
 * 바인딩은 그대로 저장돼야 한다. 그래서 「활성 ∪ 이미 고른 것」이다([keepSelectedOptions] 와 같은 규칙).
 *
 * @param {?Array} options `{id, useYn, …}` — [operatorOptionsOf] 의 결과
 * @param {?Array} selectedIds 지금 묶인 id
 */
export function bindableOrgOptions(options, selectedIds) {
  const rows = Array.isArray(options) ? options : []

  return keepSelectedOptions(
    rows,
    rows.filter((option) => !isInactiveOrg(option)),
    selectedIds
  )
}

/**
 * **고른 값 중 이름을 모르는 것이 있는가** — 있으면 후보를 다시 읽어야 한다. 순수 함수.
 *
 * 원격 검색 선택기는 후보를 질의어마다 새로 받으므로, 부모가 바인딩만 갈아 끼우면(다른 계정을 연달아
 * 여는 계정 화면) 고른 id 는 바뀌었는데 후보 목록은 앞 계정의 것이다 — 태그가 **PK 숫자로 남는다**.
 * 「비었는가」로 물으면 후보가 남아 있는 그 상태를 영영 못 고친다(`options.length > 0` 조기 반환).
 * 물어야 할 것은 **이 값의 이름을 아는가**다.
 */
export function hasUnresolvedSelection(selectedIds, options) {
  const rows = Array.isArray(options) ? options : []

  return (Array.isArray(selectedIds) ? selectedIds : []).some(
    (id) => id != null && id !== '' && !rows.some((option) => option && option.id === String(id))
  )
}

/** 「이름 (코드)」 — 이름이 없으면 코드만, 둘 다 없으면 빈 문자열이다(괄호만 남기지 않는다) */
export function orgLabel(name, code) {
  const cleanName = typeof name === 'string' ? name.trim() : ''
  const cleanCode = typeof code === 'string' ? code.trim() : ''
  if (cleanName && cleanCode) return `${cleanName} (${cleanCode})`

  return cleanName || cleanCode
}

/**
 * 고른 값이 목록에서 사라져도 남긴다 — 원격 검색은 질의어마다 후보가 바뀐다.
 * 남기지 않으면 태그가 PK 숫자로 되돌아가 **무엇에 묶였는지 못 읽는 상태**가 된다(`placeOptionsOf` 와 같은 규칙).
 */
export function keepSelectedOptions(previous, found, selectedIds) {
  const ids = (Array.isArray(selectedIds) ? selectedIds : []).map(String)
  const kept = (Array.isArray(previous) ? previous : []).filter(
    (option) => option && ids.indexOf(option.id) > -1 && !found.some((item) => item.id === option.id)
  )

  return kept.concat(found)
}

// ─── 비활성 조직 ─────────────────────────────────────────────────────────

/** 비활성 조직 행인가 — 운영사·관리점 행과 그 옵션이 같은 `useYn` 칸을 갖는다 */
export function isInactiveOrg(row) {
  return !!row && row.useYn === 'N'
}

/**
 * 운영사·관리점 관리 화면의 「비활성 포함」을 담는 **URL 질의 키** — 켠 상태가 새로고침·뒤로 가기에도 남는다
 * (매장 입점 현황이 `?agencyCd=`·`?operatorCd=` 로 필터를 받는 것과 같은 자리).
 */
export const INCLUDE_INACTIVE_QUERY = 'includeInactive'

/** URL 질의 → 「비활성 포함」이 켜져 있는가. `Y` 만 켠 것으로 본다(사람이 고친 질의가 다른 값이면 기본값) */
export function includeInactiveOf(query) {
  return !!query && query[INCLUDE_INACTIVE_QUERY] === 'Y'
}

/** 「비활성 포함」을 켜고 끈 **새** 질의 — 다른 키는 그대로 둔다. 끄면 키를 지운다(기본값은 URL 에 적지 않는다) */
export function withIncludeInactive(query, on) {
  const rest = without(query, [INCLUDE_INACTIVE_QUERY])

  return on ? Object.assign({}, rest, { [INCLUDE_INACTIVE_QUERY]: 'Y' }) : rest
}

/**
 * 목록 조회의 상태 조건 — **평소는 `useYn=Y`**, 「비활성 포함」이면 조건 없음(= 서버 기본값 전부).
 *
 * 서버 기본값(파라미터 없음 = 전부)은 파트너·다른 화면 호환 때문에 그대로이고, 숨김은 **화면의 기본값**이다
 * (R-A 브리프 §1-1). 빈 문자열을 싣지 않는다 — 조건 없음은 키가 없는 것이다.
 */
export function orgListParams(includeInactive) {
  return includeInactive ? {} : { useYn: 'Y' }
}

/**
 * 관리점 등록 409 → **「그 관리점 재활성」 버튼의 대상**. 그 409 가 아니거나 대상 PK 가 없으면 `null`.
 *
 * 같은 운영사의 비활성 관리점 담당자 휴대폰으로 새로 만들려 하면 서버는 등록을 막고 그 관리점을 가리킨다
 * (결정 38 ⑤ — 등록을 조용히 재활성으로 바꾸지 않는다). 사람이 할 일은 새로 만드는 것이 아니라 그 관리점을
 * 되살리는 것이므로 화면이 그 길을 버튼 하나로 준다. 문장은 서버 `message` 가 정본이다(여기서 짓지 않는다).
 *
 * @param {?object} body 409 응답 본문(`{code, agencyCd, agencyCode, message}`)
 * @returns {?{agencyCd: number, code: string, actionText: string}}
 */
export function inactiveAgencyConflictOf(body) {
  if (!body || body.code !== AGENCY_CONFLICT.INACTIVE_SAME_MOBILE) return null
  const agencyCd = toNumberOrNull(body.agencyCd)
  if (agencyCd == null) return null
  const code = typeof body.agencyCode === 'string' ? body.agencyCode.trim() : ''

  return { agencyCd, code, actionText: `${code} ${CALLPOP_TEXT.agencyOnAction}`.trim() }
}

// ─── 매장 입점 현황 행 ───────────────────────────────────────────────────

/**
 * 한 행의 **소속** — 운영사·관리점을 각각의 낱말로 답한다(§3-4).
 *
 * `agency` 가 `null` 이면 운영사 직할이고, 그 운영사가 `hqYn:"Y"` 면 「총괄사 직할」이다. 매핑이 아예
 * 없으면(운영사도 없음) 「매핑 없음」이다 — 세 갈래를 화면마다 적으면 한 곳만 고쳐도 말이 갈린다.
 */
export function storeOwnerView(row) {
  const operator = (row && row.operator) || null
  const agency = (row && row.agency) || null

  if (!operator) return { operatorText: CALLPOP_TEXT.mappingNone, agencyText: '', direct: false, mapped: false }

  const operatorText = orgLabel(operator.name || operator.operatorNm, operator.code)
  if (agency) {
    return {
      operatorText,
      agencyText: orgLabel(agency.name || agency.agencyNm, agency.code),
      direct: false,
      mapped: true,
    }
  }

  return {
    operatorText,
    agencyText: operator.hqYn === 'Y' ? CALLPOP_TEXT.transferHqDirect : CALLPOP_TEXT.transferDirect,
    direct: true,
    mapped: true,
  }
}

/** 소프트 삭제된 매장인가 — 매핑 행이 남아 목록에 계속 보인다(서버 보고서 §10 2) */
export function isInactiveStore(row) {
  return !!row && row.useYn === 'N'
}

// ─── 참여 조건 ───────────────────────────────────────────────────────────

/** 조건 미달 사유 → 한글. 모르는 코드는 **코드 그대로** — 서버가 어휘를 늘려도 사실을 감추지 않는다 */
export function ineligibleReasonText(reason) {
  if (reason === INELIGIBLE_REASON.NO_MID) return CALLPOP_TEXT.reasonNoMid
  if (reason === INELIGIBLE_REASON.NO_MENU) return CALLPOP_TEXT.reasonNoMenu
  if (reason === INELIGIBLE_REASON.TRANSITION_FAILED) return CALLPOP_TEXT.reasonTransitionFailed

  return reason || ''
}

/** 사유 목록 → 한 줄 */
export function ineligibleReasonsText(reasons) {
  return (Array.isArray(reasons) ? reasons : []).map(ineligibleReasonText).filter(Boolean).join(' · ')
}

/**
 * 조건 칸의 chip — `{text, tagType, eligible, reasons}`. 조건 축이 아예 없는 응답(구 서버)은 `null` 이다.
 *
 * 「없음」과 「미달」은 다른 말이다: 앞은 「이 서버는 조건 축을 모른다」이고 뒤는 「이 매장이 못 채웠다」다.
 * (`partnerLink.participationOf` 가 참여 축에 두는 것과 같은 구분이다.)
 */
export function eligibilityChip(eligibility) {
  if (!eligibility) return null
  if (eligibility.eligible) {
    return { text: CALLPOP_TEXT.eligibilityEligible, tagType: 'success', eligible: true, reasons: [] }
  }

  const reasons = Array.isArray(eligibility.reasons) ? eligibility.reasons : []

  return {
    text: reasons.length > 0 ? ineligibleReasonsText(reasons) : CALLPOP_TEXT.eligibilityIneligible,
    tagType: 'warning',
    eligible: false,
    reasons,
  }
}

/**
 * 조건 칸의 **태그 목록** — 사유 하나가 태그 하나다. 조건 축이 없는 응답(구 서버)은 빈 배열이다.
 *
 * 한 태그에 사유를 이어 붙이면 `.el-tag` 가 `white-space: nowrap` 이라 칸을 넘는 만큼 **잘린다**
 * (1440 dev 실측: 「PG(MID) 설정 없음 · 판매 중 메」). 이 칸은 「왜 미등록인가」를 읽는 자리라 잘리면
 * 기능을 잃는다 — 사유마다 한 줄씩 쌓아 전문을 보인다.
 *
 * @returns {Array<{reason: ?string, text: string, tagType: string}>}
 */
export function eligibilityTags(eligibility) {
  const chip = eligibilityChip(eligibility)
  if (!chip) return []
  if (chip.eligible || chip.reasons.length === 0) return [{ reason: null, text: chip.text, tagType: chip.tagType }]

  return chip.reasons.map((reason) => ({ reason, text: ineligibleReasonText(reason), tagType: chip.tagType }))
}

/**
 * 참여 등록 버튼을 잠글 이유 — 없으면 `null`.
 *
 * 조건 축을 **모르는** 응답에서는 잠그지 않는다: 400 `JOIN_INELIGIBLE` 이 같은 문구로 답하므로
 * (아래 [joinIneligibleText]) 화면이 「모른다」를 「미달」로 접지 않아도 사람은 같은 말을 듣는다.
 */
export function joinLockReason(eligibility) {
  const chip = eligibilityChip(eligibility)
  if (!chip || chip.eligible) return null

  return `${CALLPOP_TEXT.joinIneligibleTip}: ${chip.text}`
}

/**
 * 서버가 낸 **조건 미달 400** → 화면이 잠글 때와 같은 문구. 그 400 이 아니면 `null`.
 *
 * `HttpError` 가 본문을 들고 다니게 되면서(`plugins/http/httpError.js` 의 `data`) `reasons` 에 닿는다.
 * 그 전에는 서버의 완결된 문장에만 기대야 했는데, 그러면 **버튼을 미리 잠글 때와 눌렀을 때의 문구가
 * 다른 말**이 된다 — 같은 사실을 두 낱말로 말하는 자리였다. 이제 둘 다 [joinLockReason] 을 지난다.
 *
 * @param {?object} body 400 응답 본문(`{code, reasons, message}`)
 */
export function joinIneligibleText(body) {
  if (!body || body.code !== 'JOIN_INELIGIBLE') return null

  return joinLockReason({ eligible: false, reasons: body.reasons }) || body.message || ''
}

/**
 * 신규 매장 등록의 **409** → 1단계를 다시 세울 답. 그 409 가 아니면 `null`.
 *
 * 1단계에서 `NEW` 였던 번호가 저장 시점에는 형제를 요구할 수 있다(그 사이 누가 만들었다). 서버가
 * 후보 목록을 함께 주므로 화면은 **그 목록으로 1단계를 다시 세운다** — 문장만 받으면 사람은 2단계에
 * 갇힌 채 같은 저장을 되풀이한다. 응답에 번호가 없으면 화면이 알고 있던 번호를 쓴다.
 *
 * @param {?object} body 409 응답 본문(`{code, message, stores}`)
 * @param {?string} fallbackBizNo 화면이 확인했던 번호
 * @returns {?{bizNo: string, result: object}}
 */
/**
 * 409 가 **계정 ID 채번 경합**인가 — 사업자번호 충돌이 아닌 409 다.
 *
 * 등록 경계가 계정 저장을 생성 전용(AccountService.create)으로 바꾸면서 도달 가능한 응답이 됐다.
 * 화면이 409 를 사업자번호 충돌로만 읽으면 그 답은 「이미 있는 계정입니다.」라는 서버 문구로 흘러
 * 사람이 자기 입력을 의심한다 — **다시 누르면 풀리는 실패**라고 말하는 것이 옳다.
 *
 * **판정이 부정형인 이유**: 서버의 계정 충돌 응답에는 코드 칸이 없다(`AccountService.create` 가
 * 문구만 싣는다). 사업자번호 409 는 코드를 실으므로 그쪽을 먼저 가른 뒤 남는 409 가 이것이다.
 * 2026-09-22 서버 세 파일을 대조해 **이 경로의 비-사업자번호 409 는 계정 충돌 하나뿐**임을 확인했다
 * (등록 서비스는 400·403·503 만 던지고, 자동 참여의 409 는 `tryJoin` 이 삼킨다).
 * 서버가 이 경로에 409 를 하나 더 만드는 날 화면이 거짓말을 하므로, 그때는 서버가
 * `code: "ACCOUNT_ID_CONFLICT"` 를 싣고 이 함수가 **긍정형**으로 판정해야 한다.
 */
export function isAccountIdConflict(status, body) {
  return status === 409 && bizNoConflictView(body) == null
}

export function bizNoConflictView(body, fallbackBizNo) {
  const code = body && body.code
  if (code !== BIZ_NO_CONFLICT.SIBLING_REQUIRED && code !== BIZ_NO_CONFLICT.EXISTS_HIDDEN) return null

  const status = code === BIZ_NO_CONFLICT.SIBLING_REQUIRED ? BIZ_NO_CHECK.EXISTS_VISIBLE : BIZ_NO_CHECK.EXISTS_HIDDEN

  return {
    bizNo: String((body && body.bizNo) || fallbackBizNo || '').trim(),
    result: bizNoCheckView({ status, stores: body.stores, message: body.message }),
  }
}

// ─── 매핑·이관 ───────────────────────────────────────────────────────────

/**
 * **운영사 단위의 이관 목적지** — 각 운영사의 「직할」이 한 칸이고, 총괄사는 「총괄사 직할」로 읽힌다.
 *
 * 관리점 목적지는 후보가 수백이 될 수 있어 목록으로 세우지 않고 원격 검색(`AgencyPicker`)이 답한다 —
 * 그래서 이 함수는 **운영사 축만** 만든다. 비활성 운영사는 뺀다(서버가 400 으로 막는 목적지다).
 * 현재 위치(그 운영사 직할)는 목적지가 아니므로 뺀다.
 *
 * @param {?Array} operators `GET /callpop/operators` 의 항목
 * @param {?object} current 현재 매핑(`{operator:{operatorCd}, agency:{agencyCd}|null}`)
 * @returns {Array<{key: string, label: string, operatorCd: *, agencyCd: null, hqYn: string}>}
 */
export function operatorDestinations(operators, current) {
  const currentOperatorCd = (current && current.operator && current.operator.operatorCd) || null
  const currentAgencyCd = (current && current.agency && current.agency.agencyCd) || null
  const isCurrentDirect = (operatorCd) => currentAgencyCd == null && currentOperatorCd === operatorCd

  return operatorOptionsOf(operators)
    .filter((option) => !isInactiveOrg(option))
    .filter((option) => !isCurrentDirect(option.operatorCd))
    .map((option) => ({
      key: `OPERATOR-${option.operatorCd}`,
      label: `${option.label} — ${option.hqYn === 'Y' ? CALLPOP_TEXT.transferHqDirect : CALLPOP_TEXT.transferDirect}`,
      operatorCd: option.operatorCd,
      agencyCd: null,
      hqYn: option.hqYn,
    }))
}

/** 매핑 저장 본문 — 빈 사유·메모는 키를 싣지 않는다(빈 문자열은 「값이 있다」는 다른 뜻이다) */
export function mappingBody(destination, reason, memo) {
  const body = {
    operatorCd: destination ? destination.operatorCd : null,
    agencyCd: destination ? destination.agencyCd : null,
  }
  const cleanReason = trimmed(reason)
  const cleanMemo = trimmed(memo)
  if (cleanReason) body.reason = cleanReason
  if (cleanMemo) body.memo = cleanMemo

  return body
}

/** 관리점 비활성 본문 — 목적지가 없으면 본문 자체가 비고, 서버는 소속 매장이 있을 때 409 로 답한다(§3-3) */
export function deactivateBody(destination) {
  if (!destination) return {}

  return { transferTo: { operatorCd: destination.operatorCd, agencyCd: destination.agencyCd } }
}

/** 매핑 직후 자동 참여 결과 → 사람에게 할 말. 모르는 값은 빈 문자열(조용히 지나간다) */
export function autoJoinText(autoJoin) {
  if (autoJoin === AUTO_JOIN.JOINED) return CALLPOP_TEXT.autoJoinJoined
  if (autoJoin === AUTO_JOIN.ALREADY_ACTIVE) return CALLPOP_TEXT.autoJoinAlreadyActive
  if (autoJoin === AUTO_JOIN.ENDED) return CALLPOP_TEXT.autoJoinEnded
  if (autoJoin === AUTO_JOIN.INELIGIBLE) return CALLPOP_TEXT.autoJoinIneligible
  if (autoJoin === AUTO_JOIN.FAILED) return CALLPOP_TEXT.autoJoinFailed

  return ''
}

/** 자동 참여 실패는 **경고**다 — 매핑은 저장됐으므로 오류로 말하면 사람이 매핑을 다시 누른다 */
export function autoJoinToastType(autoJoin) {
  if (autoJoin === AUTO_JOIN.FAILED) return 'warning'
  if (autoJoin === AUTO_JOIN.JOINED) return 'success'

  return 'info'
}

// ─── 사업자번호 조회 ─────────────────────────────────────────────────────

/**
 * 사업자번호 조회 응답 → 화면 분기(§3-7).
 *
 * - `message` 가 있으면 **그 문구를 먼저** 보인다(`EXISTS_HIDDEN` 은 그것이 전부다 — 건수도 이름도 없다).
 * - `NEW` 만 바로 진행할 수 있고, `EXISTS_VISIBLE` 은 목록에서 형제 매장을 고른 뒤에 진행한다.
 *
 * @returns {{status: ?string, mode: ?string, canProceed: boolean, stores: Array, message: string}}
 */
export function bizNoCheckView(result) {
  const row = result && typeof result === 'object' ? result : {}
  const status = row.status || null
  const stores = Array.isArray(row.stores) ? row.stores.slice() : []
  const message = typeof row.message === 'string' && row.message ? row.message : bizNoReasonText(row.reason)

  return {
    status,
    mode:
      status === BIZ_NO_CHECK.NEW
        ? REGISTER_MODE.NEW
        : status === BIZ_NO_CHECK.EXISTS_VISIBLE
        ? REGISTER_MODE.SIBLING
        : null,
    canProceed: status === BIZ_NO_CHECK.NEW,
    stores,
    message,
  }
}

/** 형식 오류 사유 → 한글. 서버가 `message` 를 주면 그것이 먼저다([bizNoCheckView]) */
export function bizNoReasonText(reason) {
  if (reason === 'EMPTY') return CALLPOP_TEXT.bizReasonEmpty
  if (reason === 'LENGTH') return CALLPOP_TEXT.bizReasonLength
  if (reason === 'CHECKSUM') return CALLPOP_TEXT.bizReasonChecksum
  if (reason === 'DUMMY') return CALLPOP_TEXT.bizReasonDummy

  return ''
}

/** 사업자번호 상태 → chip */
export function bizNoStatusChip(status) {
  if (status === BIZ_NO_CHECK.NEW) return { text: CALLPOP_TEXT.bizStatusNew, tagType: 'success' }
  if (status === BIZ_NO_CHECK.EXISTS_VISIBLE) return { text: CALLPOP_TEXT.bizStatusExistsVisible, tagType: 'warning' }
  if (status === BIZ_NO_CHECK.EXISTS_HIDDEN) return { text: CALLPOP_TEXT.bizStatusExistsHidden, tagType: 'info' }
  if (status === BIZ_NO_CHECK.INVALID) return { text: CALLPOP_TEXT.bizStatusInvalid, tagType: 'danger' }

  return null
}

/**
 * 신규 매장 등록 본문(§3-8) — 빈 값은 `null` 로 싣는다(서버가 선택 칸으로 받는다).
 * `SIBLING` 일 때만 `siblingOf` 가 붙는다 — 없는 모드에 키를 남기면 서버가 다른 뜻으로 읽을 자리가 생긴다.
 */
export function storeRegisterBody(form) {
  const row = form || {}
  const body = {
    mode: row.mode === REGISTER_MODE.SIBLING ? REGISTER_MODE.SIBLING : REGISTER_MODE.NEW,
    busiNum: trimmed(row.busiNum) || null,
    ceoNm: trimmed(row.ceoNm) || null,
    custNm: trimmed(row.custNm) || null,
    storeNm: trimmed(row.storeNm) || null,
    tel: trimmed(row.tel) || null,
    storeAddr: trimmed(row.storeAddr) || null,
    storeAddrDtl: trimmed(row.storeAddrDtl) || null,
    zipCd: trimmed(row.zipCd) || null,
    lat: toNumberOrNull(row.lat),
    lng: toNumberOrNull(row.lng),
    categoryCode: trimmed(row.categoryCode) || null,
    busiEmail: trimmed(row.busiEmail) || null,
  }

  if (body.mode === REGISTER_MODE.SIBLING) body.siblingOf = row.siblingOf != null ? row.siblingOf : null

  return body
}

/**
 * 등록 결과 카드의 **계정 줄** — 서버가 그 값을 주지 않으면 줄 자체를 만들지 않는다.
 *
 * 사업장 계정 두 칸(`custMngAccountId`·`custMngInitialPassword`)이 **언제 채워지는지는 2026-09-21 에
 * 뒤집혔다**(후속 2 §1-2): 종전에는 `mode=NEW` 가 매장마다 사업장과 그 계정을 만들었지만, 이제 `NEW` 는
 * 「콜팝」 사업장에 매장만 달고 **계정을 만들지 않는다**. 반대로 `SIBLING` 은 대표 매장이 샵인샵 그룹
 * 사업장에 있지 않을 때 그룹 사업장을 새로 만들며 **그때 한 번 계정을 발급한다**.
 *
 * 화면은 그 갈래를 **몰라도 된다** — 판정은 키의 유무가 아니라 **값**이기 때문이다. 서버가 값을 주면
 * 줄이 서고 아니면 서지 않는다. 키로 가르면 값이 `null` 인 응답에서 빈 비밀번호 줄이 서고, 그것은
 * 「계정은 있는데 비밀번호를 못 받았다」는 거짓말이 된다. 규칙이 또 바뀌어도 이 함수는 그대로다.
 */
export function registerResultAccounts(result) {
  const row = result && typeof result === 'object' ? result : {}
  const rows = []
  if (row.storeMngAccountId) {
    rows.push({
      key: 'store',
      label: CALLPOP_TEXT.resultStoreAccountLabel,
      accountId: row.storeMngAccountId,
      password: row.initialPassword || '',
    })
  }
  if (row.custMngAccountId) {
    rows.push({
      key: 'cust',
      label: CALLPOP_TEXT.resultCustAccountLabel,
      accountId: row.custMngAccountId,
      password: row.custMngInitialPassword || '',
    })
  }

  return rows
}

// ─── 일괄 참여 결과 ──────────────────────────────────────────────────────

/**
 * 일괄 참여 응답 → 결과 표(§3-9). **전부-아니면-전무가 아니다** — 네 갈래가 각자 건수를 갖는다.
 *
 * 여러 묶음으로 나눠 보낸 응답을 합칠 수 있어야 하므로 **배열**을 받는다(한 번이면 한 칸짜리 배열).
 *
 * @param {?Array} results `POST …/callpop/join-bulk` 응답들
 */
export function joinBulkSummary(results) {
  const rows = Array.isArray(results) ? results.filter(Boolean) : []
  const joined = rows.reduce((all, row) => all.concat(toArray(row.joined)), [])
  const alreadyActive = rows.reduce((all, row) => all.concat(toArray(row.alreadyActive)), [])
  const skipped = rows.reduce((all, row) => all.concat(toArray(row.skipped)), [])
  const notFound = rows.reduce((all, row) => all.concat(toArray(row.notFound)), [])
  const outOfScope = rows.reduce((total, row) => total + (typeof row.outOfScope === 'number' ? row.outOfScope : 0), 0)
  const requested = rows.reduce(
    (total, row) => total + ((row.summary && typeof row.summary.requested === 'number' && row.summary.requested) || 0),
    0
  )

  return {
    requested,
    joined,
    alreadyActive,
    skipped,
    notFound,
    outOfScope,
    groups: [
      { key: 'joined', label: CALLPOP_TEXT.joinBulkJoined, tagType: 'success', count: joined.length, storeCds: joined },
      {
        key: 'alreadyActive',
        label: CALLPOP_TEXT.joinBulkAlreadyActive,
        tagType: 'info',
        count: alreadyActive.length,
        storeCds: alreadyActive,
      },
      {
        key: 'skipped',
        label: CALLPOP_TEXT.joinBulkSkipped,
        tagType: 'warning',
        count: skipped.length,
        storeCds: skipped.map((row) => (row && row.storeCd != null ? row.storeCd : row)),
        rows: skipped.map((row) => ({
          storeCd: row && row.storeCd,
          reasonsText: ineligibleReasonsText(row && row.reasons),
        })),
      },
      {
        key: 'notFound',
        label: CALLPOP_TEXT.joinBulkNotFound,
        tagType: 'danger',
        count: notFound.length,
        storeCds: notFound,
      },
      { key: 'outOfScope', label: CALLPOP_TEXT.joinBulkOutOfScope, tagType: 'info', count: outOfScope, storeCds: [] },
    ].filter((group) => group.count > 0),
  }
}

// ─── 엑셀 업로드 ─────────────────────────────────────────────────────────

/** 미리 보기·업로드 응답의 요약 한 줄 */
export function importSummaryText(summary) {
  const row = summary || {}
  const total = row.total || 0
  const create = row.create || 0
  const error = row.error || 0

  return `${CALLPOP_TEXT.joinBulkRequested} ${total}행 · ${CALLPOP_TEXT.importResultCreate} ${create}행 · ${CALLPOP_TEXT.importResultError} ${error}행`
}

/** 오류 행이 있는가 — 있으면 실제 업로드는 400 이고 **아무것도 저장되지 않는다**(§3-12) */
export function importHasError(result) {
  const rows = (result && Array.isArray(result.rows) && result.rows) || []

  return rows.some((row) => row && row.error) || !!(result && result.summary && result.summary.error > 0)
}

/** 엑셀 응답의 행 — 오류 행을 강조하려고 표에 넘길 모양으로 접는다 */
export function importRowsOf(result) {
  return ((result && Array.isArray(result.rows) && result.rows) || []).map((row) => ({
    row: row.row,
    operatorCode: row.operatorCode || '',
    code: row.code || '',
    agencyNm: row.agencyNm || '',
    mgrNm: row.mgrNm || '',
    mobile: row.mobile || '',
    memo: row.memo || '',
    result: row.result || '',
    resultText: row.result === 'ERROR' ? CALLPOP_TEXT.importResultError : CALLPOP_TEXT.importResultCreate,
    message: row.message || '',
    error: !!row.error,
  }))
}

// ─── 이력 ────────────────────────────────────────────────────────────────

/** 대상 종류 → 한글. 모르는 값은 그대로 */
export function historyTargetText(targetTp) {
  if (targetTp === HISTORY_TARGET.OPERATOR) return CALLPOP_TEXT.targetOperator
  if (targetTp === HISTORY_TARGET.AGENCY) return CALLPOP_TEXT.targetAgency
  if (targetTp === HISTORY_TARGET.STORE_MAP) return CALLPOP_TEXT.targetStoreMap

  return targetTp || ''
}

/** 동작 9종 → 한글. 모르는 값은 그대로 */
export function historyActionText(action) {
  const table = {
    [HISTORY_ACTION.CREATED]: CALLPOP_TEXT.actionCreated,
    [HISTORY_ACTION.UPDATED]: CALLPOP_TEXT.actionUpdated,
    [HISTORY_ACTION.DEACTIVATED]: CALLPOP_TEXT.actionDeactivated,
    [HISTORY_ACTION.REACTIVATED]: CALLPOP_TEXT.actionReactivated,
    [HISTORY_ACTION.IMPORTED]: CALLPOP_TEXT.actionImported,
    [HISTORY_ACTION.MAPPED]: CALLPOP_TEXT.actionMapped,
    [HISTORY_ACTION.TRANSFERRED]: CALLPOP_TEXT.actionTransferred,
    [HISTORY_ACTION.WAIVED]: CALLPOP_TEXT.actionWaived,
    [HISTORY_ACTION.UNMAPPED]: CALLPOP_TEXT.actionUnmapped,
  }

  return table[action] || action || ''
}

/**
 * 이력 `detail` 안의 칸 이름 → 한글. 모르는 키는 **키 그대로** 보인다(감추지 않는다).
 *
 * **PK 칸(`operatorCd`·`toOperatorCd`·…)은 여기에 적지 않는다** — [refAxisRuleOf] 의 접미 규칙이 이름표와
 * 이름 사전을 함께 준다. 여기에 남는 것은 그 규칙이 **모르는 뜻**뿐이다(「옮긴 운영사」는 축이 아니라 방향).
 */
const DETAIL_FIELD_TEXT = Object.freeze({
  code: CALLPOP_TEXT.operatorCodeLabel,
  operatorNm: CALLPOP_TEXT.operatorNmLabel,
  agencyNm: CALLPOP_TEXT.agencyNmLabel,
  name: CALLPOP_TEXT.agencyNmLabel,
  hqYn: CALLPOP_TEXT.hqYnLabel,
  mgrNm: CALLPOP_TEXT.mgrNmLabel,
  mgrTel: CALLPOP_TEXT.mgrTelLabel,
  mobile: CALLPOP_TEXT.mobileLabel,
  memo: CALLPOP_TEXT.memoLabel,
  useYn: CALLPOP_TEXT.stateColumn,
  toOperatorCd: CALLPOP_TEXT.movedToOperatorLabel,
  movedStoreCount: CALLPOP_TEXT.movedStoreCountLabel,
})

/** 수를 담는 칸의 **단위** — 값 뒤에 붙는다(「옮긴 매장: 2곳」). 이름표 사전과 섞지 않는다 */
const DETAIL_FIELD_UNIT = Object.freeze({ movedStoreCount: CALLPOP_TEXT.storeCountUnit })

/**
 * `detail_json` → **한글 한 줄**. 세 모양을 한 함수가 읽는다(§3-5):
 *
 * 1. 매장 매핑 — `{fromOperatorCd, toOperatorCd, fromAgencyCd, toAgencyCd, reason}`
 * 2. 마스터 수정 — `{before:{…}, after:{…}}` → **바뀐 칸만** 「라벨: 이전 → 이후」
 * 3. 마스터 생성 — 스냅샷 하나 → 「라벨: 값」
 *
 * 값 이름이 아니라 **코드(PK)** 만 든 자리는 코드를 그대로 보인다 — 이력 행에 이름이 없고, 화면이 그것을
 * 지어내면 그 순간부터 이력이 사실과 다른 말을 한다.
 */
export function historyDetailSummary(row, refs) {
  const detail = row && row.detail
  if (!detail || typeof detail !== 'object') return ''

  const targetTp = row && row.targetTp
  if (targetTp === HISTORY_TARGET.STORE_MAP) return mappingDetailSummary(detail, refs)
  if (detail.before || detail.after) return diffSummary(detail.before, detail.after, refs, targetTp)

  return snapshotSummary(detail, refs, targetTp)
}

/**
 * 이력 행의 **대상** — 「관리점 B7B 강남 (CJ0001)」. 사전에 없으면 「관리점 6」이다.
 *
 * @param {?object} row 이력 행
 * @param {?object} refs [refsOf] 의 결과
 */
export function historyTargetLabel(row, refs) {
  const targetTp = row && row.targetTp
  const cd = row && row.targetCd
  const axis = HISTORY_TARGET_AXIS[targetTp] || null
  const name = axis ? refLabel(refs, axis, cd) : null

  return `${historyTargetText(targetTp)} ${name || (cd != null ? String(cd) : '')}`.trim()
}

/** 대상 종류 → [refsOf] 의 축 이름 */
const HISTORY_TARGET_AXIS = Object.freeze({
  [HISTORY_TARGET.OPERATOR]: 'operators',
  [HISTORY_TARGET.AGENCY]: 'agencies',
  [HISTORY_TARGET.STORE_MAP]: 'stores',
})

function mappingDetailSummary(detail, refs) {
  const operatorText = (cd) => (cd == null ? '없음' : refLabel(refs, 'operators', cd) || String(cd))
  // 관리점 칸의 `null` 은 「없음」이 아니라 **직할**이다
  const agencyText = (cd) => (cd == null ? CALLPOP_TEXT.transferDirect : refLabel(refs, 'agencies', cd) || String(cd))
  const parts = []

  if (detail.fromOperatorCd != null || detail.toOperatorCd != null) {
    parts.push(
      `${CALLPOP_TEXT.operatorLabel}: ${operatorText(detail.fromOperatorCd)} → ${operatorText(detail.toOperatorCd)}`
    )
  }
  if (detail.fromAgencyCd !== undefined || detail.toAgencyCd !== undefined) {
    parts.push(`${CALLPOP_TEXT.agencyLabel}: ${agencyText(detail.fromAgencyCd)} → ${agencyText(detail.toAgencyCd)}`)
  }
  if (detail.reason) parts.push(`${CALLPOP_TEXT.transferReasonLabel}: ${detail.reason}`)

  return parts.join(' · ')
}

function diffSummary(before, after, refs, targetTp) {
  const from = before && typeof before === 'object' ? before : {}
  const to = after && typeof after === 'object' ? after : {}
  const keys = Object.keys(from)
    .concat(Object.keys(to))
    .filter((key, index, all) => all.indexOf(key) === index)

  return keys
    .filter((key) => valueText(key, from[key], refs) !== valueText(key, to[key], refs))
    .map((key) => `${fieldText(key, targetTp)}: ${valueText(key, from[key], refs)} → ${valueText(key, to[key], refs)}`)
    .join(' · ')
}

function snapshotSummary(detail, refs, targetTp) {
  return Object.keys(detail)
    .filter((key) => detail[key] != null && detail[key] !== '')
    .map((key) => `${fieldText(key, targetTp)}: ${valueText(key, detail[key], refs)}`)
    .join(' · ')
}

/**
 * 칸 이름 — **`code` 는 대상마다 다른 것을 가리킨다**. 관리점 이력의 `code` 는 관리점 코드인데 표를 하나로
 * 두면 화면이 「운영사 코드: CJ0001」이라고 **거짓을 말한다**(compose 실측에서 드러났다).
 */
function fieldText(key, targetTp) {
  if (key === 'code') {
    return targetTp === HISTORY_TARGET.AGENCY ? CALLPOP_TEXT.agencyCodeLabel : CALLPOP_TEXT.operatorCodeLabel
  }

  const rule = refAxisRuleOf(key)

  return DETAIL_FIELD_TEXT[key] || (rule && rule.label) || key
}

/**
 * `detail` 안에서 **PK 를 담는 칸** — 키마다 적지 않고 **접미 하나로** 잡는다.
 *
 * 서버는 같은 축을 접두만 바꿔 싣는다(`operatorCd`·`toOperatorCd`·`fromOperatorCd`). 키를 낱낱이 적어 두면
 * 새 접두가 생길 때마다 화면이 숫자를 보인다 — 권리 포기의 `toOperatorCd` 가 그렇게 샜다(dev 실측 D2).
 * 매핑 네 칸은 [mappingDetailSummary] 가 전후로 따로 읽는다.
 */
const REF_AXIS_RULES = Object.freeze([
  Object.freeze({ tail: 'OperatorCd', axis: 'operators', label: CALLPOP_TEXT.operatorLabel }),
  Object.freeze({ tail: 'AgencyCd', axis: 'agencies', label: CALLPOP_TEXT.agencyLabel }),
  Object.freeze({ tail: 'StoreCd', axis: 'stores', label: CALLPOP_TEXT.storeLabel }),
])

/** 칸 이름 → 그 축의 규칙. 맨 이름(`operatorCd`)과 접두가 붙은 이름(`toOperatorCd`)을 같은 자가 읽는다 */
function refAxisRuleOf(key) {
  const name = typeof key === 'string' ? key : ''

  return (
    REF_AXIS_RULES.filter((rule) => {
      const bare = rule.tail.charAt(0).toLowerCase() + rule.tail.slice(1)

      return name === bare || (name.length > rule.tail.length && name.slice(-rule.tail.length) === rule.tail)
    })[0] || null
  )
}

function valueText(key, value, refs) {
  if (value == null || value === '') return '없음'
  // 상태 칸의 Y/N 은 「예/아니오」가 아니라 활성/비활성이다 — 목록의 태그와 같은 낱말을 쓴다
  if (key === 'useYn') return value === 'N' ? CALLPOP_TEXT.inactiveTag : CALLPOP_TEXT.activeTag
  if (value === 'Y') return '예'
  if (value === 'N') return '아니오'

  const rule = refAxisRuleOf(key)
  if (rule) return refLabel(refs, rule.axis, value) || String(value)

  const unit = DETAIL_FIELD_UNIT[key]

  return unit ? `${value}${unit}` : String(value)
}

// ─── 날짜 ────────────────────────────────────────────────────────────────

/**
 * **날짜만 남긴다** — PG 계약 완료일이 화면에서 시각을 달고 다니지 않게 하는 한 자리.
 *
 * 서버는 이제 날짜만 준다(§3-11 `"YYYY-MM-DD"`). 그래도 이 함수가 있는 이유는 둘이다: 옛 응답
 * (`"YYYY-MM-DD HH:mm:ss"`)이 섞여 와도 열과 입력 칸이 **같은 값**을 보이고, 잘라내기가 호출부마다
 * 복제되지 않는다(종전에는 다이얼로그가 `slice(0, 10)` 를 직접 했다). 날짜로 안 읽히면 받은 것을 그대로
 * 돌려준다 — 화면이 값을 지어내거나 감추지 않는다.
 */
export function dateOnly(value) {
  if (value == null || value === '') return ''
  const text = String(value).trim()
  const match = text.match(/^\d{4}-\d{2}-\d{2}/)

  return match ? match[0] : text
}

/**
 * 시각을 **두 줄로 나눠 앉히기 위한 조각** — `{date, time}`. 순수 함수.
 *
 * 좁은 칸에 한 문자열로 두면 이 앱의 전역 `word-break` 가 「2026-09-20 0 / 3:22:00」처럼 **아무 데서나**
 * 끊는다(1440 실측). 끊는 자리를 화면이 정하는 것이, 값을 자르지 않고 열 폭을 줄이는 길이다(§2-5).
 *
 * 시각이 없는 값(날짜만 오는 칸·빈 값)은 `time` 이 빈 문자열이라 둘째 줄이 서지 않는다.
 */
export function stampView(value) {
  const date = dateOnly(value)
  const match = String(value == null ? '' : value)
    .trim()
    .match(/^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2}(?::\d{2})?)/)

  return { date: date || '-', time: match ? match[1] : '' }
}

// ─── 질의 ────────────────────────────────────────────────────────────────

/** 빈 값을 뺀 질의 파라미터 — 서버가 빈 문자열을 조건으로 읽지 않게 한다 */
export function cleanParams(params) {
  const row = params || {}

  return Object.keys(row).reduce((clean, key) => {
    const value = row[key]
    if (value == null || value === '') return clean

    return Object.assign({}, clean, { [key]: value })
  }, {})
}

/** 기간 두 칸 → `from`·`to`. `el-date-picker` 의 `daterange` 가 주는 배열을 읽는다 */
export function periodParams(range) {
  const rows = Array.isArray(range) ? range : []

  return { from: rows[0] || '', to: rows[1] || '' }
}

// ─── 콜팝 주문 실적(R-V A) ───────────────────────────────────────────────

/**
 * 매장 입점 현황의 **실적 기간 기본값** — 이번 달 1일 ~ 오늘(KST 달력, `periodFilter` 의 「이번달」 그대로). 순수.
 *
 * @param {number} nowMs 지금(epoch ms) — 시계는 부르는 쪽(화면)이 읽는다
 * @returns {Array<string>} `[from, to]`(`yyyy-MM-dd`) — `el-date-picker` `daterange` 의 값 모양
 */
export function ordersDefaultPeriod(nowMs) {
  const range = getDateRangeForPeriod('THIS_MONTH', nowMs)

  return [range.startDate, range.endDate]
}

/**
 * 행의 콜팝 주문 실적 두 수 — 서버 `orders`(`{count, amount}`)를 단위 붙은 자리 구분 글자로(`3건`·`28,660원`).
 * 유효 주문의 판정은 서버가 한다. 순수.
 *
 * `orders` 가 없으면(기간을 보내지 않은 응답·구 서버) 두 수 모두 `-` 다 — 「0건」과 「세지 않았다」는 다른 사실이다.
 */
export function storeOrdersView(row) {
  const orders = row && row.orders && typeof row.orders === 'object' ? row.orders : null
  if (!orders) return { countText: '-', amountText: '-' }

  return {
    countText: numberText(orders.count) + CALLPOP_TEXT.ordersCountUnit,
    amountText: numberText(orders.amount) + CALLPOP_TEXT.ordersAmountUnit,
  }
}

/**
 * 행의 앱 노출 값 — 서버 `appPlaceCount`(콜팝 소유·OPEN 플레이스 중 편성된 곳의 수)를 단위 붙은 글자로(`2곳`·`없음`). 순수.
 * 이름표(「앱 노출」)는 실적 두 수처럼 그리는 쪽이 단다.
 *
 * 참여와 **다른 축**이다: 참여 조건 미달 자동 제외(03:50)는 편성만 빼고 참여 등록은 그대로 두므로 「참여 중」인데 앱에 없을 수 있다.
 * 그 경우만 `warn` 이다 — 미등록·해지 매장이 앱에 없는 것은 당연해서 드러내지 않는다.
 * 칸이 없는 응답(구 서버)은 `-` 다 — 모르는 것을 「없음」으로 접지 않는다.
 */
export function storeAppView(row) {
  const count = row ? toNumberOrNull(row.appPlaceCount) : null
  if (count === null) return { text: '-', warn: false }
  if (count > 0) return { text: numberText(count) + CALLPOP_TEXT.appPlaceUnit, warn: false }

  const participation = row.participation || null
  return {
    text: CALLPOP_TEXT.appPlaceNone,
    warn: !!participation && participation.state === PARTICIPATION_STATE.ACTIVE,
  }
}

function trimmed(value) {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim()
}

// 숫자 읽기는 `utils/numberValue` 가 소유한다 — 콜팝 축과 플레이스 축이 같은 관문을 지난다

function toArray(value) {
  return Array.isArray(value) ? value : []
}
